import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type PlacementVendorStatus = "active" | "inactive" | "pending";

export interface PlacementVendorType {
  _id: string;
  companyName: string; // e.g. "Google Inc."
  contactPerson: string; // Renamed from hrContact
  email: string; // e.g. "emily@google.com"
  phone: string;
  website: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  salaryPackage: string; // e.g. "6-8" (LPA)
  availablePositions: number; // e.g. 10
  companyDescription: string;
  eligibilityCriteria: string;
  jobRequirements: string;
  totalPlacements: number; // Keeping for display in table if needed
  status: PlacementVendorStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PlacementVendorCreateType {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  website: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  salaryPackage: string;
  availablePositions: number;
  companyDescription: string;
  eligibilityCriteria: string;
  jobRequirements: string;
  status: PlacementVendorStatus;
}

export interface PlacementVendorMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type PlacementVendorParamsType = {
  search?: string;
  status?: PlacementVendorStatus | "all";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_PLACEMENT_VENDORS: PlacementVendorType[] = [
  {
    _id: "1",
    companyName: "Google Inc.",
    contactPerson: "Emily Chen",
    email: "emily@google.com",
    phone: "+1-650-253-0000",
    website: "https://google.com/careers",
    industry: ["Technology", "Software"],
    city: "Mountain View",
    state: "CA",
    preferredDepartments: ["1", "2"],
    salaryPackage: "12-18",
    availablePositions: 50,
    companyDescription: "Search engine giant.",
    eligibilityCriteria: "Min 8.0 CGPA",
    jobRequirements: "DSA, System Design",
    totalPlacements: 45,
    status: "active",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    _id: "2",
    companyName: "Microsoft Corporation",
    contactPerson: "Robert Smith",
    email: "robert@microsoft.com",
    phone: "+1-425-882-8080",
    website: "https://careers.microsoft.com",
    industry: ["Technology", "Software"],
    city: "Redmond",
    state: "WA",
    preferredDepartments: ["1", "2"],
    salaryPackage: "10-16",
    availablePositions: 40,
    companyDescription: "Software giant.",
    eligibilityCriteria: "Min 7.5 CGPA",
    jobRequirements: "C#, Azure, .NET",
    totalPlacements: 38,
    status: "active",
    createdAt: "2024-01-02T00:00:00.000Z",
    updatedAt: "2024-01-02T00:00:00.000Z",
  },
  {
    _id: "3",
    companyName: "Amazon",
    contactPerson: "Lisa Anderson",
    email: "lisa@amazon.com",
    phone: "+1-206-266-1000",
    website: "https://amazon.jobs",
    industry: ["E-commerce", "Technology"],
    city: "Seattle",
    state: "WA",
    preferredDepartments: ["1", "3"],
    salaryPackage: "15-25",
    availablePositions: 60,
    companyDescription: "E-commerce and Cloud Computing.",
    eligibilityCriteria: "Min 7.0 CGPA",
    jobRequirements: "Java, AWS, Problem Solving",
    totalPlacements: 32,
    status: "active",
    createdAt: "2024-01-03T00:00:00.000Z",
    updatedAt: "2024-01-03T00:00:00.000Z",
  },
  {
    _id: "4",
    companyName: "Infosys",
    contactPerson: "Priya Sharma",
    email: "priya@infosys.com",
    phone: "+91-80-2852-0261",
    website: "https://infosys.com/careers",
    industry: ["IT Services", "Consulting"],
    city: "Bangalore",
    state: "Karnataka",
    preferredDepartments: ["1", "2", "3", "4"],
    salaryPackage: "3.5-5",
    availablePositions: 100,
    companyDescription: "Global leader in consulting and IT services.",
    eligibilityCriteria: "Min 6.0 CGPA, No Active Backlogs",
    jobRequirements: "Basic Programming, Communication Skills",
    totalPlacements: 60,
    status: "active",
    createdAt: "2024-01-04T00:00:00.000Z",
    updatedAt: "2024-01-04T00:00:00.000Z",
  },
  {
    _id: "5",
    companyName: "TCS",
    contactPerson: "Rahul Mehta",
    email: "rahul@tcs.com",
    phone: "+91-22-6778-9999",
    website: "https://tcs.com/careers",
    industry: ["IT Services", "Consulting"],
    city: "Mumbai",
    state: "Maharashtra",
    preferredDepartments: ["1", "2", "3", "4"],
    salaryPackage: "3.3-4.5",
    availablePositions: 150,
    companyDescription: "IT services, consulting and business solutions.",
    eligibilityCriteria: "Min 6.0 CGPA",
    jobRequirements: "Aptitude, Coding Basics",
    totalPlacements: 0,
    status: "inactive",
    createdAt: "2024-01-05T00:00:00.000Z",
    updatedAt: "2024-01-05T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  vendors: PlacementVendorType[],
  filters: PlacementVendorParamsType
): { data: PlacementVendorType[]; total: number } => {
  let result = [...vendors];
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (v) =>
        v.companyName.toLowerCase().includes(q) ||
        v.contactPerson.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q)
    );
  }
  if (filters.status && filters.status !== "all") {
    result = result.filter((v) => v.status === filters.status);
  }
  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);
  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllPlacementVendors = async (
  filters: PlacementVendorParamsType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/vendors/placement/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_PLACEMENT_VENDORS, filters);
  return {
    vendors: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as PlacementVendorMetaType,
  };
};

export const fetchPlacementVendorById = async (_id: string) => {
  await simulateDelay();
  const vendor = DUMMY_PLACEMENT_VENDORS.find((v) => v._id === _id);
  if (!vendor) throw new Error("Placement vendor not found");
  return vendor;
};

export const createPlacementVendor = async (
  data: PlacementVendorCreateType
) => {
  await simulateDelay();
  const newVendor: PlacementVendorType = {
    ...data,
    _id: String(Date.now()),
    totalPlacements: 0, // Default for new vendor
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_PLACEMENT_VENDORS.push(newVendor);
  return newVendor;
};

export const updatePlacementVendor = async (
  _id: string,
  data: PlacementVendorCreateType
) => {
  await simulateDelay();
  const index = DUMMY_PLACEMENT_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Placement vendor not found");
  DUMMY_PLACEMENT_VENDORS[index] = {
    ...DUMMY_PLACEMENT_VENDORS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_PLACEMENT_VENDORS[index];
};

export const deletePlacementVendor = async (_id: string) => {
  await simulateDelay();
  const index = DUMMY_PLACEMENT_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Placement vendor not found");
  const [deleted] = DUMMY_PLACEMENT_VENDORS.splice(index, 1);
  return deleted;
};

// Mock Industry Options Fetcher
export const fetchIndustryOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const INDUSTRIES = [
    { label: "Technology", value: "Technology" },
    { label: "Software", value: "Software" },
    { label: "Consulting", value: "Consulting" },
    { label: "Finance", value: "Finance" },
    { label: "E-commerce", value: "E-commerce" },
    { label: "Manufacturing", value: "Manufacturing" },
    { label: "Healthcare", value: "Healthcare" },
  ];

  const filtered = INDUSTRIES.filter((i) =>
    i.label.toLowerCase().includes((params.search || "").toLowerCase())
  );

  return {
    items: filtered,
    hasMore: false,
    total: filtered.length,
  };
};
export const fetchPlacementVendorOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const search = (params.search || "").toLowerCase();
  const filtered = DUMMY_PLACEMENT_VENDORS.filter((v) =>
    v.companyName.toLowerCase().includes(search)
  );
  return {
    items: filtered.map((v) => ({
      label: v.companyName,
      value: v._id,
    })),
    hasMore: false,
    total: filtered.length,
  };
};

export const placementVendorService = {
  fetchAllPlacementVendors,
  fetchPlacementVendorById,
  createPlacementVendor,
  updatePlacementVendor,
  deletePlacementVendor,
  fetchIndustryOptions,
  fetchPlacementVendorOptions,
};
